/**
 * LOADER SCREEN (owner B1). The first beat: night land, the ridge engraved faintly, and the Whorl
 * whose rings fill with the bytes as they arrive (spec §E.7 beat 0, copy §F loader.*).
 *
 *   <LoaderScreen onEnter={…} auto={kiosk} />
 *
 *  - Calls preload.start() on mount (idempotent), so it works whoever mounts it first.
 *  - Progress comes from the store (loadedBytes / totalBytes, written by the preloader once per
 *    frame). React takes a new value at most every 120 ms; the rings' own 380 ms settle transition
 *    carries the motion between values, so nothing animates through React state.
 *  - Text is revealed after fontsReady() (≤ 3 s), so the figures never swap faces mid-count.
 *  - Ready → web: "Open the map" (Sun button) appears and takes focus if nothing else has it.
 *    Kiosk (auto): enters by itself once the rings have closed.
 *  - Enter → onEnter() at once (the intro starts beneath), preload.loadDeferred() (the DEM), the
 *    stage fades (640 `leave`; 420 `drift` on a return visit; 160 under reduced motion), then the
 *    component renders nothing.
 *  - Error → calm copy + "Try again" (also automatic when the connection returns, and on a
 *    back-off in kiosk mode). Announced assertively through the bus (spec §H.3).
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type JSX } from "react";
import type { LoaderScreenProps } from "@/contracts/ui";
import { Button, Whorl, WhorlLoader, cx, ensureRidge, fontsReady, useReducedMotion } from "@/design";
import { appStore, useApp } from "@/store/store";
import { announce } from "@/store/bus";
import { preload } from "./preload";
import { LOADER_COPY, mb, slowNote } from "./copy";
import "./loader.css";

type Stage = "loading" | "ready" | "leaving" | "gone";

/** React receives progress at most this often; CSS eases the rings between values. */
const PROGRESS_MS = 120;
/** Show loader.slow after this long under 60 %. */
const SLOW_MS = 8000;
/** Kiosk: hold the closed rings this long before entering by itself. */
const AUTO_HOLD_MS = 520;
/** Kiosk: retry back-off after an error (ms), capped at the last value. */
const AUTO_RETRY = [5000, 10000, 20000, 40000, 60000];

interface Progress {
  loaded: number;
  total: number;
}

/** Store progress, throttled to PROGRESS_MS (with a trailing update, and at once when complete). */
function useThrottledProgress(): Progress {
  const [p, setP] = useState<Progress>(() => {
    const s = appStore.getState();
    return { loaded: s.loadedBytes, total: s.totalBytes };
  });
  useEffect(() => {
    let last = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const push = () => {
      timer = undefined;
      last = performance.now();
      const s = appStore.getState();
      setP((prev) => (prev.loaded === s.loadedBytes && prev.total === s.totalBytes ? prev : { loaded: s.loadedBytes, total: s.totalBytes }));
    };
    const unsub = appStore.subscribe((s, prev) => {
      if (s.loadedBytes === prev.loadedBytes && s.totalBytes === prev.totalBytes) return;
      const complete = s.totalBytes > 0 && s.loadedBytes >= s.totalBytes;
      const wait = PROGRESS_MS - (performance.now() - last);
      if (complete || wait <= 0) {
        clearTimeout(timer);
        push();
      } else if (!timer) {
        timer = setTimeout(push, wait);
      }
    });
    push();
    return () => {
      unsub();
      clearTimeout(timer);
    };
  }, []);
  return p;
}

export interface LoaderScreenOwnProps extends LoaderScreenProps {
  /** (I1) Also wait for this before offering Enter: the map has drawn its opening frame beneath. */
  until?: Promise<unknown>;
}

export function LoaderScreen({ onEnter, auto = false, until }: LoaderScreenOwnProps): JSX.Element | null {
  const reduced = useReducedMotion();
  const introSeen = useApp((s) => s.introSeen);
  const loadError = useApp((s) => s.loadError);
  const { loaded, total } = useThrottledProgress();

  const [stage, setStage] = useState<Stage>("loading");
  const [fontsOk, setFontsOk] = useState(false);
  const [ridgeOk, setRidgeOk] = useState(false);
  const [slow, setSlow] = useState(false);

  const stageRef = useRef<Stage>("loading");
  stageRef.current = stage;
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  const enterWrapRef = useRef<HTMLSpanElement>(null);
  const figureRef = useRef<HTMLDivElement>(null);

  const p = total > 0 ? Math.min(1, loaded / total) : 0;
  const pRef = useRef(p);
  pRef.current = p;

  /* Start (idempotent) and learn when the essential set is in memory (and, with `until`, the map is drawn). */
  const untilRef = useRef(until);
  untilRef.current = until;
  useEffect(() => {
    let live = true;
    preload.start();
    void preload.essential
      .then(() => untilRef.current)
      .then(() => {
        if (live && stageRef.current === "loading") setStage("ready");
      })
      .catch(() => {
        /* the error state comes from the store (loadError) */
      });
    return () => {
      live = false;
    };
  }, []);

  /* Brand faces first, then the words. The ridge texture costs ~150 ms once: after first paint. */
  useEffect(() => {
    let live = true;
    void fontsReady().then(() => live && setFontsOk(true));
    const raf = requestAnimationFrame(() => {
      setTimeout(() => {
        if (!live) return;
        ensureRidge();
        setRidgeOk(true);
      }, 0);
    });
    return () => {
      live = false;
      cancelAnimationFrame(raf);
    };
  }, []);

  /* loader.slow: after 8 s still under 60 %. */
  useEffect(() => {
    const t = setTimeout(() => {
      if (stageRef.current === "loading" && pRef.current < 0.6) setSlow(true);
    }, SLOW_MS);
    return () => clearTimeout(t);
  }, []);

  // Full intro: 640 `leave` (spec §E.7 beat 0). Return visit (short intro): 420 `drift`. Reduced
  // motion: 160, which the design system enforces on every transition inside .ld (§E.10).
  const shortIntro = introSeen && !reduced;
  const leaveMs = reduced ? 160 : shortIntro ? 420 : 640;

  const enter = useCallback(() => {
    if (stageRef.current !== "ready") return;
    stageRef.current = "leaving";
    setStage("leaving");
    preload.loadDeferred(); // first, so the DEM starts even if onEnter throws
    onEnterRef.current();
  }, []);

  /* Unmount after the fade. */
  useEffect(() => {
    if (stage !== "leaving") return;
    const t = setTimeout(() => setStage("gone"), leaveMs + 40);
    return () => clearTimeout(t);
  }, [stage, leaveMs]);

  /* Ready: kiosk enters by itself; on the web the Enter button takes focus if nothing else has it. */
  useEffect(() => {
    if (stage !== "ready") return;
    if (auto) {
      const t = setTimeout(enter, reduced ? 0 : AUTO_HOLD_MS);
      return () => clearTimeout(t);
    }
    // The action slot mounts on this render; focus on the next frame.
    const raf = requestAnimationFrame(() => {
      const btn = enterWrapRef.current?.querySelector("button");
      const active = document.activeElement;
      if (btn && (!active || active === document.body || active === figureRef.current)) {
        btn.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [stage, auto, reduced, enter]);

  /* Errors: announce (assertive), retry when the connection returns, and on a back-off in kiosk mode. */
  const unsupported = loadError === LOADER_COPY.webgl;
  const errorDetail = unsupported ? LOADER_COPY.webglDetail : loadError === LOADER_COPY.offline ? null : LOADER_COPY.errorDetail;
  useEffect(() => {
    if (!loadError) return;
    announce(errorDetail ? `${loadError} ${errorDetail}` : loadError, "assertive");
  }, [loadError, errorDetail]);

  const retries = useRef(0);
  const retry = useCallback(() => {
    preload.start();
    // Keep focus inside the loader: the figure (tabIndex -1) now holds the progressbar.
    requestAnimationFrame(() => figureRef.current?.focus({ preventScroll: true }));
  }, []);
  useEffect(() => {
    if (!loadError || unsupported) return;
    const online = () => preload.start();
    window.addEventListener("online", online);
    let t: ReturnType<typeof setTimeout> | undefined;
    if (auto) {
      const wait = AUTO_RETRY[Math.min(retries.current, AUTO_RETRY.length - 1)];
      retries.current++;
      t = setTimeout(() => preload.start(), wait);
    }
    return () => {
      window.removeEventListener("online", online);
      clearTimeout(t);
    };
  }, [loadError, unsupported, auto]);

  if (stage === "gone") return null;

  const leaving = stage === "leaving";
  const ready = stage !== "loading";
  // Every byte can be in before the checksums and parsing finish. Hold the rings a hair short of
  // closed (still "100 %") until the preloader is truly ready, so the Enter button only appears
  // when it works.
  const shownP = ready ? 1 : Math.min(p, 0.995);
  const detail = total > 0 ? LOADER_COPY.detail(mb(ready ? total : loaded), mb(total)) : undefined;
  const rootStyle = {
    "--ld-loc-loader-leave-ms": `${leaveMs}ms`,
    "--ld-loc-loader-leave-ease": shortIntro ? "var(--ease-drift)" : "var(--ease-leave)",
  } as CSSProperties;

  return (
    <div
      className={cx("ld", "ld-loc-loader", leaving && "is-leaving")}
      style={rootStyle}
      data-motion={reduced ? "reduced" : undefined}
      data-stage={stage}
      aria-busy={stage === "loading" && !loadError}
    >
      <div className={cx("ld-loc-loader-ridge", ridgeOk && "is-on")} aria-hidden="true" />
      <div className="ld-vignette ld-loc-loader-shade" aria-hidden="true" />

      <div ref={figureRef} tabIndex={-1} className={cx("ld-loc-loader-figure", fontsOk && "is-in")}>
        {loadError && !ready ? (
          <div className="ld-loc-loader-error ld-in">
            {/* One composition for every error (no WebGL, offline, failed): the whole mark, dimmed. At the
                bytes' progress a first-visit failure (nothing loaded) would draw no rings at all. */}
            <Whorl size={112} progress={1} decorative className="ld-loc-loader-error-mark" />
            <p className="ld-loc-loader-error-title">{loadError}</p>
            {errorDetail && <p className="ld-loc-loader-error-detail">{errorDetail}</p>}
            {!unsupported && (
              <div className="ld-loc-loader-error-action">
                <Button variant="glass" onClick={retry}>
                  {LOADER_COPY.errorAction}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <WhorlLoader
              progress={shownP}
              done={leaving}
              label={LOADER_COPY.label}
              detail={detail}
              style={{ position: "relative", inset: "auto", zIndex: "auto", background: "transparent" }}
              action={
                // Kiosk: an empty placeholder keeps the slot reserved (the slow note lives there).
                auto ? (
                  <span aria-hidden="true" />
                ) : (
                  <span ref={enterWrapRef}>
                    <Button variant="sun" onClick={enter} disabled={leaving}>
                      {LOADER_COPY.enter}
                    </Button>
                  </span>
                )
              }
            />
            {/* Sits in the Enter button's reserved slot: the note gives way to the button there. */}
            <p className={cx("ld-loc-loader-note", slow && !ready && "is-on")} aria-live="polite">
              {slow && !ready
                ? slowNote(total)
                    .split(/(?<=\.) /)
                    .map((line) => <span key={line}>{line}</span>)
                : null}
            </p>
          </>
        )}
      </div>

      <p className={cx("ld-loc-loader-place", fontsOk && "is-on")} aria-hidden="true">
        {LOADER_COPY.place.split(" · ").map((s) => (
          <span key={s}>{s}</span>
        ))}
      </p>
    </div>
  );
}
